import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { RefreshCw } from 'lucide-react'

import { superAdminService } from '@/api/super-admin'
import { Subscription, SubscriptionListParams } from '@/types/super-admin'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/table'
import { DataTableColumnHeader } from '@/components/data-table-column-header'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useRouter, useSearch } from '@tanstack/react-router'
import { filterNonNullValues } from '@/lib/utils'
import { Coins, MoreHorizontal } from 'lucide-react'

interface SubscriptionsTableParams {
  page: number
  page_size: number
  ordering: string | null
  search: string | null
}

export default function SubscriptionsManagement() {
  const { t } = useTranslation()
  const searchParams = useSearch({ strict: false })
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [pagesCnt, setPagesCnt] = useState<number>(0)
  const [params, setParams] = useState<SubscriptionsTableParams>({
    page: searchParams?.page || 0,
    page_size: searchParams?.page_size || 10,
    ordering: searchParams?.ordering || null,
    search: null,
  })
  const router = useRouter()

  // Sync params to router search params
  useEffect(() => {
    router.navigate({
      to: '.',
      search: filterNonNullValues({
        page: params.page,
        page_size: params.page_size,
        ordering: params.ordering,
        search: params.search,
      }),
      replace: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  // Details Dialog State
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false)
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)

  const openDetails = async (id: string) => {
    try {
      const s = await superAdminService.getSubscription(id)
      setSelectedSubscription(s)
      setDetailsOpen(true)
    } catch (e) {
      // ignore for now, keep UX simple
    }
  }

  const priceDisplay = (cents?: number | null, currency?: string | null) => {
    if (cents == null) return '-'
    const amount = (cents / 100).toFixed(2)
    return currency ? `${amount} ${currency}` : `${amount}`
  }

  const statusBadgeVariant = (status: Subscription['status']) => {
    switch (status) {
      case 'active': return 'default'
      case 'trialing': return 'outline'
      case 'pending': return 'secondary'
      case 'past_due': return 'destructive'
      case 'paused': return 'secondary'
      case 'canceled': return 'destructive'
      case 'free': return 'outline'
      default: return 'secondary'
    }
  }

  // Columns for DataTable
  const columns: ColumnDef<Subscription>[] = useMemo(() => [
    {
      accessorKey: 'cabinet',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('cabinet') || 'Cabinet'} />
      ),
      cell: ({ row }) => (
        <div className='font-mono text-xs'>{row.original.cabinet}</div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'plan',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('plan') || 'Plan'} />
      ),
      cell: ({ row }) => <div>{row.original.plan}</div>,
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'amount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('amount') || 'Amount'} />
      ),
      cell: ({ row }) => (
        <div className='flex items-center gap-1'>
          <Coins className='h-4 w-4 text-amber-600' />
          {priceDisplay(row.original.amount_cents, row.original.currency)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status') || 'Status'} />
      ),
      cell: ({ row }) => (
        <Badge variant={statusBadgeVariant(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'current_period_end',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('current_period_end') || 'Period End'} />
      ),
      cell: ({ row }) => (
        <div className='w-fit text-nowrap'>
          {row.original.current_period_end ? new Date(typeof row.original.current_period_end === 'number' ? row.original.current_period_end * 1000 : row.original.current_period_end).toLocaleString() : '-'}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'cancel_at_period_end',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('cancel_at_period_end') || 'Cancel at End'} />
      ),
      cell: ({ row }) => (
        <div className='w-fit text-nowrap'>
          {row.original.cancel_at_period_end ? t('yes') || 'Yes' : t('no') || 'No'}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('created_at') || 'Created'} />
      ),
      cell: ({ row }) => (
        <div className='w-fit text-nowrap'>
          {row.original.created_at ? new Date(typeof row.original.created_at === 'number' ? row.original.created_at * 1000 : row.original.created_at).toLocaleString() : '-'}
        </div>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'actions',
      header: () => null,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem onClick={() => openDetails(row.original.id)}>
              {t('view_details') || 'View details'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      enableSorting: false,
      enableHiding: false,
      meta: { className: 'w-10 text-center' }
    },
  ], [t])

  // Fetch list
  useEffect(() => {
    const p: SubscriptionListParams = {
      page: params.page,
      page_size: params.page_size,
      ordering: params.ordering || undefined,
      search: params.search || undefined,
    }
    setLoading(true)
    superAdminService.getSubscriptions(p)
      .then(resp => {
        setSubscriptions(resp.results)
        setPagesCnt(Math.ceil(resp.count / params.page_size))
        setError(false)
      })
      .catch(() => {
        setError(true)
      })
      .finally(() => setLoading(false))
  }, [params])

  // Toolbar with search
  function SubscriptionsTableToolbar(
    { table: _table, params, setParams }: { table?: any; params: SubscriptionsTableParams; setParams: React.Dispatch<React.SetStateAction<SubscriptionsTableParams>> }
  ) {
    const { t } = useTranslation()

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setParams(prev => ({ ...prev, search: value === '' ? null : value }))
    }

    return (
      <div className='flex items-center justify-between'>
        <div className='flex flex-1 flex-col-reverse items-start gap-y-2 sm:flex-row sm:items-center sm:space-x-2'>
          <Input
            defaultValue={params.search || ''}
            placeholder={t('search') || 'Search'}
            onChange={handleSearchChange}
            className='h-8 w-[180px] lg:w-[260px]'
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <Main>
        <PageHeader
          leading={
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <RefreshCw className="h-5 w-5" />
            </span>
          }
          title={t('subscriptions') || 'Subscriptions'}
          description={t('subscriptions_list_desc') || 'Observe cabinet subscriptions across the system.'}
        />
        <div className='-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0'>
          {
            loading ? <DataTableSkeleton columns={6} rows={6} />
              : error ?
                <div className='py-8 text-center'>
                  {t('table_load_error') || 'Error loading table'}
                </div>
                : <DataTable
                  data={subscriptions}
                  columns={columns}
                  params={params}
                  setParams={setParams}
                  pages={pagesCnt}
                  ToolbarName={SubscriptionsTableToolbar}
                  storageKey="super-admin-subscriptions-table"
                />
          }
        </div>
      </Main>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('subscription_details') || 'Subscription Details'}</DialogTitle>
          </DialogHeader>
          {!selectedSubscription ? (
            <div className='py-4'>{t('loading') || 'Loading'}...</div>
          ) : (
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <div className='text-sm text-muted-foreground'>{t('cabinet') || 'Cabinet'}</div>
                <div className='font-mono text-xs'>{selectedSubscription.cabinet}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('plan') || 'Plan'}</div>
                <div>{selectedSubscription.plan}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('amount') || 'Amount'}</div>
                <div>{priceDisplay(selectedSubscription.amount_cents, selectedSubscription.currency)}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('status') || 'Status'}</div>
                <Badge variant={statusBadgeVariant(selectedSubscription.status)}>
                  {selectedSubscription.status}
                </Badge>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('current_period_start') || 'Period Start'}</div>
                <div>{selectedSubscription.current_period_start ? new Date(typeof selectedSubscription.current_period_start === 'number' ? selectedSubscription.current_period_start * 1000 : selectedSubscription.current_period_start).toLocaleString() : '-'}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('current_period_end') || 'Period End'}</div>
                <div>{selectedSubscription.current_period_end ? new Date(typeof selectedSubscription.current_period_end === 'number' ? selectedSubscription.current_period_end * 1000 : selectedSubscription.current_period_end).toLocaleString() : '-'}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('trial_end') || 'Trial End'}</div>
                <div>{selectedSubscription.trial_end ? new Date(typeof selectedSubscription.trial_end === 'number' ? selectedSubscription.trial_end * 1000 : selectedSubscription.trial_end).toLocaleString() : '-'}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('cancel_at_period_end') || 'Cancel at End'}</div>
                <div>{selectedSubscription.cancel_at_period_end ? (t('yes') || 'Yes') : (t('no') || 'No')}</div>
              </div>
              <div>
                <div className='text-sm text-muted-foreground'>{t('created_at') || 'Created'}</div>
                <div>{selectedSubscription.created_at ? new Date(typeof selectedSubscription.created_at === 'number' ? selectedSubscription.created_at * 1000 : selectedSubscription.created_at).toLocaleString() : '-'}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}